import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { Plus, Trash2, GripVertical, X, Settings } from 'lucide-react';
import api from '../services/api';

const ENTITY_TABS = [
  { key: 'lead', label: 'Leads' },
  { key: 'task', label: 'Tasks' },
  { key: 'note', label: 'Notes' }
];

const TYPE_OPTIONS = [
  { value: 'text',     label: 'Text (single line)' },
  { value: 'textarea', label: 'Textarea (multi-line)' },
  { value: 'number',   label: 'Number' },
  { value: 'date',     label: 'Date' },
  { value: 'dropdown', label: 'Dropdown' },
  { value: 'checkbox', label: 'Checkbox' }
];

const EMPTY_FORM = { label: '', type: 'text', options: [], required: false };

const CustomFields = () => {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('lead');
  const [form, setForm] = useState(EMPTY_FORM);
  const [optionInput, setOptionInput] = useState('');

  const { data: defs = [], isLoading } = useQuery(
    ['custom-fields', activeTab],
    () => api.get('/custom-fields', { params: { entity: activeTab } }).then((r) => r.data)
  );

  const createMutation = useMutation(
    (body) => api.post('/custom-fields', body).then((r) => r.data),
    {
      onSuccess: () => {
        toast.success('Field added');
        queryClient.invalidateQueries(['custom-fields', activeTab]);
        setForm(EMPTY_FORM);
        setOptionInput('');
      },
      onError: (err) => toast.error(err.response?.data?.error || 'Create failed')
    }
  );

  const patchMutation = useMutation(
    ({ id, patch }) => api.patch(`/custom-fields/${id}`, patch).then((r) => r.data),
    {
      onSuccess: () => queryClient.invalidateQueries(['custom-fields', activeTab]),
      onError: (err) => toast.error(err.response?.data?.error || 'Update failed')
    }
  );

  const deleteMutation = useMutation(
    (id) => api.delete(`/custom-fields/${id}`).then((r) => r.data),
    {
      onSuccess: () => { toast.success('Deleted'); queryClient.invalidateQueries(['custom-fields', activeTab]); },
      onError: (err) => toast.error(err.response?.data?.error || 'Delete failed')
    }
  );

  const reorderMutation = useMutation(
    (order) => api.post('/custom-fields/reorder', { entity: activeTab, order }).then((r) => r.data),
    {
      onSuccess: () => queryClient.invalidateQueries(['custom-fields', activeTab]),
      onError: () => queryClient.invalidateQueries(['custom-fields', activeTab])
    }
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.label.trim()) return toast.error('Label is required');
    if (form.type === 'dropdown' && form.options.length === 0) return toast.error('Add at least one option');
    createMutation.mutate({ entity: activeTab, ...form });
  };

  const addOption = () => {
    const v = optionInput.trim();
    if (!v) return;
    setForm((f) => ({ ...f, options: [...f.options, v] }));
    setOptionInput('');
  };

  const removeOption = (idx) => {
    setForm((f) => ({ ...f, options: f.options.filter((_, i) => i !== idx) }));
  };

  const handleDragEnd = (result) => {
    if (!result.destination || result.destination.index === result.source.index) return;
    const reordered = Array.from(defs);
    const [moved] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, moved);
    const withOrder = reordered.map((d, i) => ({ id: d._id, order: i }));
    queryClient.setQueryData(['custom-fields', activeTab], reordered);
    reorderMutation.mutate(withOrder);
  };

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
          <Settings size={28} /> Custom Fields
        </h1>
        <p className="text-sm text-gray-600 mt-1">
          Define your own fields on leads, tasks and notes. Dropped fields keep any saved values on existing records.
        </p>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-gray-200 mb-6">
        {ENTITY_TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setActiveTab(t.key)}
            className={`px-4 py-2 text-sm font-medium transition-colors -mb-px border-b-2 ${activeTab === t.key ? 'border-primary-600 text-primary-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Existing fields */}
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden mb-6">
        <div className="px-4 py-3 bg-gray-50 border-b border-gray-100 grid grid-cols-12 gap-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
          <div className="col-span-1"></div>
          <div className="col-span-4">Label</div>
          <div className="col-span-2">Key</div>
          <div className="col-span-3">Type</div>
          <div className="col-span-1">Req.</div>
          <div className="col-span-1 text-right"></div>
        </div>
        {isLoading ? (
          <p className="p-4 text-sm text-gray-400">Loading...</p>
        ) : defs.length === 0 ? (
          <p className="p-6 text-sm text-gray-400 italic text-center">No custom fields for {activeTab}s yet.</p>
        ) : (
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="fields">
              {(provided) => (
                <div ref={provided.innerRef} {...provided.droppableProps}>
                  {defs.map((def, index) => (
                    <Draggable key={def._id} draggableId={def._id} index={index}>
                      {(prov, snapshot) => (
                        <div
                          ref={prov.innerRef}
                          {...prov.draggableProps}
                          className={`grid grid-cols-12 gap-2 items-center px-4 py-3 border-b border-gray-100 last:border-b-0 ${snapshot.isDragging ? 'bg-blue-50 shadow' : 'hover:bg-gray-50'}`}
                        >
                          <div className="col-span-1" {...prov.dragHandleProps}>
                            <GripVertical className="w-4 h-4 text-gray-400 cursor-grab" />
                          </div>
                          <div className="col-span-4">
                            <input
                              defaultValue={def.label}
                              onBlur={(e) => {
                                if (e.target.value.trim() && e.target.value !== def.label) {
                                  patchMutation.mutate({ id: def._id, patch: { label: e.target.value.trim() } });
                                }
                              }}
                              className="w-full px-2 py-1 border border-transparent hover:border-gray-300 focus:border-primary-500 focus:outline-none rounded text-sm"
                            />
                          </div>
                          <div className="col-span-2 text-xs font-mono text-gray-500 truncate" title={def.key}>{def.key}</div>
                          <div className="col-span-3 text-xs text-gray-600">
                            {def.type}
                            {def.type === 'dropdown' && (
                              <div className="mt-1 flex flex-wrap gap-1">
                                {def.options.map((o, i) => (
                                  <span key={i} className="text-[10px] bg-gray-100 px-1.5 py-0.5 rounded">{o}</span>
                                ))}
                              </div>
                            )}
                          </div>
                          <div className="col-span-1">
                            <input
                              type="checkbox"
                              checked={def.required}
                              onChange={(e) => patchMutation.mutate({ id: def._id, patch: { required: e.target.checked } })}
                            />
                          </div>
                          <div className="col-span-1 text-right">
                            <button
                              onClick={() => window.confirm(`Delete "${def.label}"? Saved values on existing records are kept but hidden.`) && deleteMutation.mutate(def._id)}
                              className="text-gray-400 hover:text-red-600"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        )}
      </div>

      {/* Add new */}
      <form onSubmit={handleSubmit} className="bg-white border border-gray-200 rounded-xl shadow-sm p-4 space-y-3">
        <h2 className="text-sm font-semibold text-gray-700">Add new field</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <input
            value={form.label}
            onChange={(e) => setForm({ ...form, label: e.target.value })}
            placeholder="Label (e.g. Industry)"
            className="px-3 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
          <select
            value={form.type}
            onChange={(e) => setForm({ ...form, type: e.target.value, options: [] })}
            className="px-3 py-2 text-sm border border-gray-200 rounded-md"
          >
            {TYPE_OPTIONS.map((t) => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
        </div>
        {form.type === 'dropdown' && (
          <div className="bg-gray-50 p-3 rounded-md">
            <div className="flex items-center gap-2 mb-2">
              <input
                value={optionInput}
                onChange={(e) => setOptionInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addOption(); } }}
                placeholder="Add option and press Enter"
                className="flex-1 px-2 py-1 text-sm border border-gray-200 rounded-md"
              />
              <button type="button" onClick={addOption} className="px-3 py-1 text-xs bg-gray-200 hover:bg-gray-300 rounded">Add</button>
            </div>
            <div className="flex flex-wrap gap-1">
              {form.options.map((o, i) => (
                <span key={i} className="inline-flex items-center gap-1 bg-white px-2 py-1 rounded-full text-xs border border-gray-200">
                  {o}
                  <button type="button" onClick={() => removeOption(i)} className="text-gray-400 hover:text-red-600">
                    <X size={10} />
                  </button>
                </span>
              ))}
            </div>
          </div>
        )}
        <div className="flex items-center justify-between">
          <label className="inline-flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.required} onChange={(e) => setForm({ ...form, required: e.target.checked })} />
            Required
          </label>
          <button
            type="submit"
            disabled={createMutation.isLoading}
            className="inline-flex items-center gap-1 px-4 py-2 bg-primary-600 hover:bg-primary-700 disabled:opacity-50 text-white rounded-md text-sm font-medium"
          >
            <Plus size={14} /> Add field
          </button>
        </div>
      </form>
    </div>
  );
};

export default CustomFields;
